import { LucideIcon } from "lucide-react";

interface Action {
    label: string;
    icon?: LucideIcon;
    onClick: () => void;
    variant?: "primary" | "secondary" | "danger" | "ghost";
    disabled?: boolean;
}

interface PageHeaderProps {
    title: string;
    description?: string;
    actions?: Action[];
    badge?: string | number;
}

export function PageHeader({ title, description, actions, badge }: PageHeaderProps) {
    return (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
                <div className="w-1.5 h-10 bg-primary rounded-r-full hidden sm:block" />
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight text-secondary">
                            {title}
                        </h1>
                        {badge !== undefined && (
                            <span className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-sm">
                                {badge}
                            </span>
                        )}
                    </div>
                    {description && (
                        <p className="text-text-muted text-sm mt-0.5">{description}</p>
                    )}
                </div>
            </div>

            {actions && actions.length > 0 && (
                <div className="flex items-center gap-2 sm:gap-3">
                    {actions.map((action, i) => {
                        const variants: Record<string, string> = {
                            primary: "bg-secondary text-white hover:bg-primary-dark shadow-sm hover:shadow-md",
                            secondary: "bg-white text-secondary border border-accent/20 hover:bg-surface hover:border-primary/20",
                            danger: "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200",
                            ghost: "text-text-muted hover:text-primary hover:bg-surface",
                        };
                        const v = action.variant || "primary";
                        return (
                            <button
                                key={i}
                                onClick={action.onClick}
                                disabled={action.disabled}
                                className={`
                                    flex items-center gap-2 px-4 py-2.5 rounded-sm font-medium text-sm transition-all duration-200
                                    ${variants[v]}
                                    ${action.disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}
                                `}
                            >
                                {action.icon && <action.icon className="w-4 h-4" strokeWidth={2} />}
                                {action.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
